import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Search } from "lucide-react";
import { allBlogArticles } from "../data/blogArticles";
import { kbArticles } from "../data/knowledgeBase";
import { seoPages, SITE } from "../data/seoPages";
import { allAgencyPages } from "../data/agencyPages";

const buildIndex = () => [
  ...allBlogArticles.map((b) => ({ t: b.title, p: `/blog/${b.id}`, k: "Blog", x: b.category || "" })),
  ...kbArticles.map((a) => ({ t: a.q, p: `/knowledge/${a.slug}`, k: "Knowledge Base", x: a.cluster })),
  ...seoPages.map((s) => ({ t: s.kw, p: `/software/${s.slug}`, k: "AI Software", x: s.cluster })),
  ...allAgencyPages.map((a) => ({ t: a.kw, p: `/${a.slug}`, k: "Services", x: "" })),
  { t: "Client Case Studies", p: "/case-studies", k: "Case Studies", x: "" },
  { t: "Growth Story — Videos & Dashboards", p: "/growth-story", k: "Case Studies", x: "" },
  { t: "Services Overview", p: "/services", k: "Services", x: "" },
  { t: "Certificates & Partnerships", p: "/certificates", k: "About", x: "" },
  { t: "FAQs", p: "/faq", k: "Services", x: "" },
  { t: "India Marketplace Onboarding Report 2026", p: "/reports/marketplace-onboarding-report-2026", k: "Reports", x: "" },
];

export default function SiteSearchPage() {
  const [q, setQ] = useState("");
  const index = useMemo(buildIndex, []);
  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (query.length < 2) return [];
    const words = query.split(/\s+/);
    return index
      .map((item) => {
        const hay = `${item.t} ${item.k} ${item.x}`.toLowerCase();
        const score = words.reduce((s, w) => s + (hay.includes(w) ? 1 : 0), 0);
        return { ...item, score };
      })
      .filter((i) => i.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 40);
  }, [q, index]);

  return (
    <div className="min-h-screen bg-white pt-24 pb-16" data-testid="site-search-page">
      <Helmet>
        <title>Search BrandBorn | Guides, Services, Software & FAQs</title>
        <meta name="description" content="Search all BrandBorn content: 115+ blog guides, knowledge base, AI software pages, onboarding services and case studies." />
        <link rel="canonical" href={`${SITE}/search`} />
      </Helmet>
      <div className="max-w-3xl mx-auto px-4">
        <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-6">Search BrandBorn</h1>
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search onboarding, Myntra, AI listings, documents, Blinkit..."
            data-testid="site-search-input"
            className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-gray-200 rounded-2xl text-base outline-none focus:ring-2 focus:ring-red-400"
          />
        </div>
        {q.trim().length >= 2 && (
          <p className="text-sm text-gray-500 font-semibold mb-4" data-testid="site-search-count">{results.length} results across {index.length}+ pages</p>
        )}
        <div className="space-y-2">
          {results.map((r) => (
            <Link key={r.p + r.t} to={r.p} className="flex items-center justify-between gap-3 p-4 bg-white border border-gray-100 rounded-xl hover:border-red-200 hover:bg-red-50/40 transition-colors" data-testid="site-search-result">
              <span className="font-bold text-sm text-gray-800">{r.t}</span>
              <span className="text-[10px] font-black uppercase text-gray-400 flex-shrink-0">{r.k}</span>
            </Link>
          ))}
        </div>
        {q.trim().length < 2 && (
          <div className="text-sm text-gray-500">
            <p className="font-bold text-gray-700 mb-3">Popular searches:</p>
            <div className="flex flex-wrap gap-2">
              {["Myntra onboarding", "AI listing software", "Blinkit vendor", "documents required", "Nykaa", "catalog management"].map((s) => (
                <button key={s} onClick={() => setQ(s)} className="px-4 py-2 bg-slate-100 rounded-full text-xs font-bold text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors">{s}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
